/* eslint-disable react-hooks/exhaustive-deps */
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { API_CALL_SEARCH_TEXT_LENGTH, setApiCallTextLength } from '../../constants';
import { useSidebarPinContext, useSmallScreen } from '../../hooks';
import { useBoolFlag } from '../flags';
import { AppView } from './view';

interface Props extends PropsWithChildren {
  accountId?: string;
  renderDrawerContent: (open: boolean) => JSX.Element;
  siteTitle: string;
}

export const AppController: FC<Props> = ({ accountId, children, renderDrawerContent, siteTitle }) => {
  const smallScreen = useSmallScreen();
  const drawerRef = useRef<HTMLDivElement>(undefined);
  const { isPinned } = useSidebarPinContext();
  const [drawerFocused, setDrawerFocused] = useState(false);
  const open = smallScreen ? drawerFocused : (drawerFocused || isPinned);
  const isTwoCharSearchEnabled = useBoolFlag('portals-enable-2-character-search-temporary-feature');

  useEffect(() => {
    setApiCallTextLength(isTwoCharSearchEnabled ? 2 : API_CALL_SEARCH_TEXT_LENGTH);
  }, [isTwoCharSearchEnabled]);

  useEffect(() => {
    setDrawerFocused(false);
  }, [useSmallScreen()]);

  const keyDownHandler = useCallback((event: KeyboardEvent): void => {
    switch (event.key) {
      case 'N':
        if (event.shiftKey && event.altKey) {
          setDrawerFocused(!drawerFocused);

          if (drawerRef.current) {
            drawerRef.current.querySelector<HTMLElement>('a, button, [role="button"]')?.focus();
          }
        }
        break;
    }
  }, [drawerFocused, drawerRef]);

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler);
    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [keyDownHandler]);

  useEffect(() => {
    document.body.classList.toggle('small-screen', smallScreen);
  }, [smallScreen]);

  useEffect(() => {
    Array.from(document.body.classList)
      .filter((className) => className.startsWith('account-'))
      .forEach((className) => {
        document.body.classList.remove(className);
      });

    if (accountId) {
      document.body.classList.add(`account-${accountId}`);
    }
  }, [accountId]);

  return (
    <AppView
      drawerContent={renderDrawerContent(open)}
      drawerOpen={open}
      drawerRef={drawerRef}
      onDrawerClose={() => { setDrawerFocused(false); }}
      onDrawerOpen={() => setDrawerFocused(true)}
      siteTitle={siteTitle}
    >
      {children}
    </AppView>
  );
};
